import { useQuery } from '@tanstack/react-query';

import { ReferralLinkResponse, useEmployersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';

/**
 * Free days granted per referral when the backend does not say otherwise
 * (an older backend omits `credits_inviter` / `credits_invitee`).
 */
export const DEFAULT_REFERRAL_CREDITS = 3;

/** Price of one PPU day, used to show what the free days are worth. */
const PPU_DAY_PRICE_EUR = 19.9;

export type ReferralCredits = {
  /** Free days the inviter receives for each referral. */
  inviter: number;
  /** Free days the invited organization receives. */
  invitee: number;
  /** Rounded euro value of the inviter's free days. */
  inviterValueEur: number;
  /** Rounded euro value of the invitee's free days. */
  inviteeValueEur: number;
};

/** Derives the referral amounts from a `referral_link` payload, falling back to 3 days each. */
export function getReferralCredits(
  data?: Pick<ReferralLinkResponse, 'credits_inviter' | 'credits_invitee'> | null
): ReferralCredits {
  const inviter = data?.credits_inviter ?? DEFAULT_REFERRAL_CREDITS;
  const invitee = data?.credits_invitee ?? DEFAULT_REFERRAL_CREDITS;

  return {
    inviter,
    invitee,
    inviterValueEur: Math.round(inviter * PPU_DAY_PRICE_EUR),
    inviteeValueEur: Math.round(invitee * PPU_DAY_PRICE_EUR),
  };
}

/**
 * Referral amounts for the logged-in employer. Reads the shared `['referral-link']`
 * query, so it never adds a request when another consumer already loaded it.
 * The queryFn unwraps the AxiosResponse like every other consumer of that key
 * (see available-credits.tsx).
 */
export function useReferralCredits(): ReferralCredits {
  const { user } = useAuth();
  const employersService = useEmployersService();

  const { data } = useQuery({
    queryKey: ['referral-link'],
    queryFn: async () => (await employersService.getReferralLink()).data,
    enabled: !!user?.using_ppu_business_model,
  });

  return getReferralCredits(data);
}
